import * as Sentry from '@sentry/react';
import { SentryConfig } from '~/core/configuration/configuration.type';

const sentryOptions: Sentry.BrowserOptions = {
    ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.',
        // Avast extension error
        '_avast_submit',
    ],
    denyUrls: [
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
    integrations: [
        Sentry.thirdPartyErrorFilterIntegration({
            // Specify the application keys that you specified in the Sentry bundler plugin
            filterKeys: ['searchready-SERP'],

            // Defines how to handle errors that contain third party stack frames.
            // Possible values are:
            // - 'drop-error-if-contains-third-party-frames'
            // - 'drop-error-if-exclusively-contains-third-party-frames'
            // - 'apply-tag-if-contains-third-party-frames'
            // - 'apply-tag-if-exclusively-contains-third-party-frames'
            behaviour: 'drop-error-if-contains-third-party-frames',
        }),
    ],
    attachStacktrace: true,
};

export const sentryInit = (config?: SentryConfig) => {
    const dsn = process.env.PUBLIC_SENTRY_DSN;
    const environment = process.env.PUBLIC_SENTRY_ENVIRONMENT || 'local';
    const supportedEnvironments = ['prod'];

    if (config?.enabled === false) {
        console.warn('Sentry is not enabled, Sentry will not be initialized.');
        return;
    }

    if (!supportedEnvironments.includes(environment) && !process.env.FORCE_SENTRY) {
        console.warn(`Sentry does not run in the "${environment}" environment, Sentry will not be initialized.`);
        return;
    }

    if (!process.env.PUBLIC_SENTRY_DSN) {
        console.warn('Sentry DSN is not set (missing env var "PUBLIC_SENTRY_DSN", Sentry will not be initialized.');
        return;
    }
    if (!process.env.PUBLIC_SENTRY_ENVIRONMENT) {
        console.warn(
            'Sentry environment is not set (missing env var "PUBLIC_SENTRY_ENVIRONMENT", Sentry will be initialized with "local" environment.'
        );
    }

    if (!supportedEnvironments.includes(environment)) {
        console.warn(
            `Sentry environment "${environment}" is not supported. Supported environments are: ${supportedEnvironments.join(
                ', '
            )}.`
        );
    }

    // The sample rate is a number between 0.0 and 1.0 that represents the percentage of session-long replays that should be sent to Sentry.
    // You may want to change it to 100% while in development and then sample at a lower rate in production.
    const replaysSessionSampleRate = environment === 'local' || environment === 'dev' ? 1.0 : 0.1;

    Sentry.init({
        ...sentryOptions,
        environment,
        dsn,
        // Performance Monitoring
        tracesSampleRate: config?.tracesSampleRate ?? 0.1,
        // Session Replay
        replaysSessionSampleRate: config?.replaysSessionSampleRate ?? replaysSessionSampleRate,
        replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 0.1,
        debug: environment === 'local' || environment === 'dev',
    });
};

export const sentryInitNoConfig = () => {
    const environment = process.env.PUBLIC_SENTRY_ENVIRONMENT || 'local';

    Sentry.init({
        ...sentryOptions,
        environment: process.env.PUBLIC_SENTRY_ENVIRONMENT || 'local',
        dsn: process.env.PUBLIC_SENTRY_DSN,
        // Performance Monitoring
        tracesSampleRate: environment === 'local' || environment === 'dev' ? 1 : 0.1,
        // Session Replay
        replaysSessionSampleRate: environment === 'local' || environment === 'dev' ? 1.0 : 0.1,
        replaysOnErrorSampleRate: 0.1,
        debug: environment === 'local' || environment === 'dev',
        attachStacktrace: true,
    });
};
